import type {
  Candidate,
  AfcSubmissionPosition,
  AfcSubmissionCandidateOpportunity,
  AfcSubmission,
  AhqSubmissionCandidateOpportunity,
} from '@axiom/validation';
import { CurrencyUtil } from '@axiom/utils';
import type { CurrencySymbolTypes, CurrencyCodeKeyTypes } from '@axiom/const';
import { CandidateOpportunitiesConst } from '@axiom/const';

const { hourly, daily, weekly } =
  CandidateOpportunitiesConst.DisplayBillingRate;
const { DisplayBillingRate } = CandidateOpportunitiesConst;

const RateIncrement = {
  DAY: 'day',
  HOUR: 'hour',
  WEEK: 'week',
} as const;

// Create a type from the values
type RateIncrementType = (typeof RateIncrement)[keyof typeof RateIncrement];

const getRateObject = ({
  currency,
  price,
  nonDiscountedRate,
  increment,
}: {
  currency: CurrencySymbolTypes | null;
  price: number;
  nonDiscountedRate: number | null;
  increment: RateIncrementType;
}) => {
  const currencyLabel = currency || '';
  return {
    rate: `${currencyLabel}${CurrencyUtil.getFormattedCurrency(price)}`,
    nonDiscountedRate: nonDiscountedRate
      ? `${currencyLabel}${CurrencyUtil.getFormattedCurrency(nonDiscountedRate)}`
      : null,
    increment,
  };
};

const getRate = ({
  proposedHourlyRate,
  displayBillingRate,
  billingHoursPerDay,
  nonDiscountedRate,
  currency,
}: {
  proposedHourlyRate?: number | null;
  displayBillingRate?:
    | (typeof DisplayBillingRate)[keyof typeof DisplayBillingRate]
    | null;
  billingHoursPerDay: number | null;
  nonDiscountedRate: number | null;
  currency: CurrencySymbolTypes | null;
}) => {
  let price = proposedHourlyRate;
  if (!price || price < 0) {
    return '--';
  }

  const hoursPerDay = billingHoursPerDay ?? 0;

  switch (displayBillingRate) {
    case hourly: {
      return getRateObject({
        currency,
        price,
        nonDiscountedRate,
        increment: RateIncrement.HOUR,
      });
    }
    case daily: {
      if (hoursPerDay > 0) {
        price *= hoursPerDay;
        return getRateObject({
          currency,
          price,
          nonDiscountedRate,
          increment: RateIncrement.DAY,
        });
      }
      break;
    }
    case weekly: {
      if (hoursPerDay > 0) {
        price *= hoursPerDay * 5;
        return getRateObject({
          currency,
          price,
          nonDiscountedRate,
          increment: RateIncrement.WEEK,
        });
      }
      break;
    }
    default: {
      return '--';
    }
  }

  return '--';
};

export const CandidateProposedRateUtil = {
  formatCandidateRateLegacy(
    candidate: AhqSubmissionCandidateOpportunity,
    currency: CurrencySymbolTypes | null
  ) {
    const { displayBillingRate, proposedHourlyRate, position } = candidate;

    const rateData = getRate({
      proposedHourlyRate,
      displayBillingRate,
      billingHoursPerDay: position.billingHoursPerDay || null,
      nonDiscountedRate: null,
      currency,
    });

    if (typeof rateData === 'string') {
      return rateData;
    }

    return `${rateData.rate}/${rateData.increment}`;
  },
  formatCandidateRate(candidateId: Candidate['id'], submission: AfcSubmission) {
    const currency = CurrencyUtil.convertToSymbol(
      submission.currency as CurrencyCodeKeyTypes
    );
    let displayBillingRate:
      | AfcSubmissionCandidateOpportunity['displayBillingRate']
      | null = null;
    let proposedHourlyRate:
      | AfcSubmissionCandidateOpportunity['proposedHourlyRate']
      | null = null;
    let nonDiscountedRate:
      | AfcSubmissionCandidateOpportunity['nonDiscountedRate']
      | null = null;

    const position = submission.positions.find((pos: AfcSubmissionPosition) => {
      const data = pos.candidateOpportunities.find(
        (co: AfcSubmissionCandidateOpportunity) =>
          co.candidate.id === candidateId
      );

      if (data) {
        displayBillingRate = data.displayBillingRate;
        proposedHourlyRate = data.proposedHourlyRate;
        nonDiscountedRate = data.nonDiscountedRate;
      }

      return !!data;
    });

    const billingHoursPerDay = position?.billingHoursPerDay || null;

    return getRate({
      proposedHourlyRate,
      displayBillingRate,
      billingHoursPerDay,
      nonDiscountedRate,
      currency,
    });
  },
  shouldDisplayRate(candidateOpportunity: AfcSubmissionCandidateOpportunity) {
    return !!(
      candidateOpportunity.displayBillingRate &&
      candidateOpportunity.displayBillingRate !==
        CandidateOpportunitiesConst.DisplayBillingRate.none &&
      candidateOpportunity.proposedHourlyRate
    );
  },
};
